import React, { useState, useEffect, useCallback } from 'react';
import { adminApiService } from '../../services/adminApi';

const emptyForm = { tokens: 10, bonusTokens: 0, price: 900, active: true, order: 0 };

// Per-court-type credit-pack manager (F2). Opened from CourtTypeManagement.
const CreditPackModal = ({ courtType, idToken, onClose }) => {
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchPacks = useCallback(async () => {
    try {
      setLoading(true);
      setPacks(await adminApiService.getCreditPacks(idToken, courtType.id));
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [idToken, courtType.id]);

  useEffect(() => { fetchPacks(); }, [fetchPacks]);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setShowForm(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ tokens: p.tokens, bonusTokens: p.bonusTokens || 0, price: p.price, active: p.active, order: p.order || 0 });
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!(Number(form.tokens) > 0) || !(Number(form.price) >= 20)) {
      setError('โทเคนต้องมากกว่า 0 และราคาต้องอย่างน้อย 20 บาท');
      return;
    }
    try {
      setSaving(true);
      const payload = { ...form, tokens: Number(form.tokens), bonusTokens: Number(form.bonusTokens) || 0, price: Number(form.price), order: Number(form.order) || 0, courtTypeId: courtType.id };
      if (editing) await adminApiService.updateCreditPack(idToken, editing.id, payload);
      else await adminApiService.createCreditPack(idToken, payload);
      setShowForm(false);
      await fetchPacks();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm('ลบแพ็กเกจนี้?')) return;
    try { await adminApiService.deleteCreditPack(idToken, p.id); await fetchPacks(); }
    catch (err) { setError(err.message); }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-bold text-gray-900">แพ็กเกจเครดิต</h3>
              <p className="text-sm text-gray-500">{courtType.thaiName || courtType.name}</p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
          </div>

          {error && <div className="mb-3 p-2 bg-red-50 border border-red-200 text-red-700 rounded text-sm">{error}</div>}

          {loading ? (
            <p className="text-center py-6 text-gray-400">กำลังโหลด...</p>
          ) : (
            <ul className="space-y-2 mb-4">
              {packs.length === 0 && <li className="text-sm text-gray-400 text-center py-4 border border-dashed rounded-lg">ยังไม่มีแพ็กเกจ</li>}
              {packs.map((p) => (
                <li key={p.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                  <div>
                    <span className="font-semibold text-gray-900">{p.tokens}{p.bonusTokens ? ` +${p.bonusTokens}🎁` : ''} โทเคน</span>
                    <span className="text-gray-500"> · ฿{p.price.toLocaleString()}</span>
                    {!p.active && <span className="ml-2 text-xs text-gray-400">(ปิดใช้งาน)</span>}
                  </div>
                  <div className="flex gap-2 text-sm">
                    <button onClick={() => openEdit(p)} className="text-blue-600 hover:text-blue-800">แก้ไข</button>
                    <button onClick={() => remove(p)} className="text-red-600 hover:text-red-800">ลบ</button>
                  </div>
                </li>
              ))}
            </ul>
          )}

          {showForm ? (
            <form onSubmit={submit} className="border-t pt-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <label className="text-sm">โทเคน
                  <input type="number" min="1" step="0.5" value={form.tokens} onChange={(e) => setForm({ ...form, tokens: e.target.value })} className="mt-1 w-full px-3 py-2 border rounded-lg" />
                </label>
                <label className="text-sm">โบนัส (แถม)
                  <input type="number" min="0" step="0.5" value={form.bonusTokens} onChange={(e) => setForm({ ...form, bonusTokens: e.target.value })} className="mt-1 w-full px-3 py-2 border rounded-lg" />
                </label>
                <label className="text-sm">ราคา (บาท)
                  <input type="number" min="20" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="mt-1 w-full px-3 py-2 border rounded-lg" />
                </label>
                <label className="text-sm">ลำดับ
                  <input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} className="mt-1 w-full px-3 py-2 border rounded-lg" />
                </label>
              </div>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> เปิดใช้งาน
              </label>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setShowForm(false)} className="px-3 py-2 text-sm text-gray-600">ยกเลิก</button>
                <button type="submit" disabled={saving} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium disabled:opacity-50">{saving ? '...' : (editing ? 'บันทึก' : 'เพิ่ม')}</button>
              </div>
            </form>
          ) : (
            <button onClick={openCreate} className="w-full py-2 border border-blue-200 text-blue-600 rounded-lg text-sm font-medium hover:bg-blue-50">+ เพิ่มแพ็กเกจ</button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreditPackModal;
